import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import type { ForumAuthor } from '~/api/forum/types';
import type { useForumPosts } from '~/api/forum/useForumPosts';
import type { useForumSavedPosts } from '~/api/forum/useForumSavedPosts';
import type { useForumUserPosts } from '~/api/forum/useForumUserPosts';

import { Button, Dialog, ErrorState } from '~/components';
import { useIntersectionObserver } from '~/hooks/useIntersectionObserver';

import { ForumAuthorDialog } from './ForumAuthorDialog';
import { ForumPostPreview } from './ForumPostPreview';
import { ForumPostPreviewSkeleton } from './ForumPostPreview.skeleton';
import { bottomActionStyle, emptyMessageStyle, postsStyle } from './ForumPosts.css';

type Props = {
  emptyMessage: string;
  errorMessage: string;
  page: 'timeline' | 'selfPosts' | 'savedPosts';
  query: ReturnType<typeof useForumPosts> | ReturnType<typeof useForumUserPosts> | ReturnType<typeof useForumSavedPosts>;
};

export const ForumPosts = ({ emptyMessage, errorMessage, page, query }: Props) => {
  const { data, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isPending, isSuccess, refetch } = query;
  const handleFetchNextPage = useCallback(() => !isFetchingNextPage && fetchNextPage(), [fetchNextPage, isFetchingNextPage]);
  const posts = isSuccess ? data.pages.flatMap(({ data }) => data) : [];

  const { ref: showMoreButtonRef } = useIntersectionObserver(handleFetchNextPage, { threshold: 0 });
  const { isIntersecting: isFirstPostVisible, ref: firstPostRef } = useIntersectionObserver(null, { threshold: 0.2 });

  const authorDialogHandle = Dialog.useHandle<ForumAuthor>();
  const { pathname, search } = useLocation();

  const { t } = useTranslation(['common', 'forum']);

  if (isError) return <ErrorState onRetry={refetch} title={errorMessage} />;

  if (isPending) {
    return (
      <div className={postsStyle}>
        {Array.from({ length: 3 }).map((_, i) => (
          <ForumPostPreviewSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (posts.length === 0) return <div className={emptyMessageStyle}>{emptyMessage}</div>;

  return (
    <div className={postsStyle}>
      {posts.map((post, i) => (
        <ForumPostPreview
          authorDialogHandle={authorDialogHandle}
          from={`${pathname}${search}`}
          key={post.id}
          post={post}
          showPinned={page === 'timeline'}
          {...(i === 0 && { ref: firstPostRef })}
        />
      ))}

      {hasNextPage && (
        <Button
          className={bottomActionStyle}
          emphasis="tertiary"
          loading={isFetchingNextPage}
          onClick={() => fetchNextPage()}
          ref={showMoreButtonRef}
        >
          {!isFetchingNextPage && t('common:infiniteScroll.showMore')}
        </Button>
      )}

      {!hasNextPage && !isFirstPostVisible && (
        <Button className={bottomActionStyle} emphasis="tertiary" icon="ArrowUp" onClick={() => window.scrollTo(0, 0)}>
          {t('common:infiniteScroll.scrollToTop')}
        </Button>
      )}

      <ForumAuthorDialog handle={authorDialogHandle} />
    </div>
  );
};
